import {
  Briefcase,
  Calendar,
  Clock,
  Key,
  MapPin,
  Shield,
  User,
} from "lucide-react";
import {
  FilterType,
  FilterOperator,
  FilterCondition,
} from "../components/FilterBar";
import { MFReviewAccount } from "state/members/accessReviews";
import { MFSelectableOptions } from "state/members/options";
import { FaIdBadge } from "react-icons/fa";
import { FilterOption } from "components/FilterDropdown";

/**
 * Defines the structure of a filter configuration
 */
export interface FilterConfig {
  type: FilterType;
  label: string;
  icon: any;
  operators: FilterOperator[];
  valueType: "text" | "date" | "select" | "multiselect";
  options?: FilterOption[];
  optionsSource?: keyof MFSelectableOptions;
  description?: string;
  applyFilter: (
    account: MFReviewAccount,
    operator: FilterOperator,
    value: any,
  ) => boolean;
}

/**
 * Registry of all available filters
 */
export const FILTERS_REGISTRY: Record<FilterType, FilterConfig> = {
  location: {
    type: "location",
    label: "Location",
    icon: MapPin,
    operators: ["equals", "contains", "isOneOf", "isEmpty"],
    valueType: "text",
    optionsSource: "location_countries",
    description: "Filter by member location",
    applyFilter: (account, operator, value) => {
      const memberLocation = account.member.location || "";
      return applyFilterOperation(memberLocation, operator, value);
    },
  },
  department: {
    type: "department",
    label: "Department",
    icon: Briefcase,
    operators: ["equals", "contains", "isOneOf", "isEmpty"],
    valueType: "text",
    optionsSource: "departments",
    description: "Filter by member department",
    applyFilter: (account, operator, value) => {
      const department = account.member.department || "";
      return applyFilterOperation(department, operator, value);
    },
  },
  jobTitle: {
    type: "jobTitle",
    label: "Job Title",
    icon: FaIdBadge,
    operators: ["equals", "contains", "isOneOf", "isEmpty"],
    valueType: "text",
    optionsSource: "job_titles",
    description: "Filter by member job title",
    applyFilter: (account, operator, value) => {
      const jobTitle = account.member.jobTitle || "";
      return applyFilterOperation(jobTitle, operator, value);
    },
  },
  // Was "Access Level", reading account.role. Both that field and accessLevel
  // are gone from the payload; they only ever held the entitlement names, which
  // is what this always meant to filter on. No optionsSource: the previous one
  // ("roles") listed the org's distinct resource_access access levels, a
  // different value set entirely, so a suggested value matched nothing unless
  // the strings coincided. With no options the input falls back to free text
  // (see FilterInputs), which matches what this filters on.
  accessLevel: {
    type: "accessLevel",
    label: "Entitlements",
    icon: Key,
    operators: ["equals", "contains", "isOneOf", "isEmpty"],
    valueType: "text",
    description: "Filter by entitlements held in the application",
    applyFilter: (account, operator, value) => {
      const names = (account.entitlements || [])
        .map((e) => e.snapshotEntitlementName)
        .filter(Boolean)
        .join(", ");
      return applyFilterOperation(names, operator, value);
    },
  },
  accessGrantedAt: {
    type: "accessGrantedAt",
    label: "Access Granted At",
    icon: Calendar,
    operators: ["before", "after", "isEmpty"],
    valueType: "date",
    description: "Filter by when access was granted",
    applyFilter: (account, operator, value) => {
      if (!account.accessGrantedAt) {
        return operator === "isEmpty";
      }

      if (operator === "isEmpty") {
        return !account.accessGrantedAt;
      }

      const grantedDate = new Date(account.accessGrantedAt);
      const filterDate = new Date(value);

      if (operator === "before") {
        return grantedDate < filterDate;
      } else if (operator === "after") {
        return grantedDate > filterDate;
      }
      return true;
    },
  },
  lastUsage: {
    type: "lastUsage",
    label: "Last Usage",
    icon: Clock,
    operators: ["before", "after", "isEmpty"],
    valueType: "date",
    description: "Filter by when access was last used",
    applyFilter: (account, operator, value) => {
      const lastUsage = account.lastUsageDate
        ? typeof account.lastUsageDate === "string"
          ? account.lastUsageDate
          : account.lastUsageDate?.date
        : null;

      if (!lastUsage) {
        return operator === "isEmpty";
      }

      if (operator === "isEmpty") {
        return !lastUsage;
      }

      const lastUsageDate = new Date(lastUsage);
      const filterDate = new Date(value);

      if (operator === "before") {
        return lastUsageDate < filterDate;
      } else if (operator === "after") {
        return lastUsageDate > filterDate;
      }
      return true;
    },
  },
  memberStatus: {
    type: "memberStatus",
    label: "Member Status",
    icon: User,
    operators: ["equals", "isOneOf"],
    valueType: "select",
    options: [
      { label: "Active", value: "active" },
      { label: "Inactive", value: "inactive" },
    ],
    description: "Filter by member status",
    applyFilter: (account, operator, value) => {
      // Default to active if not specified
      const memberStatus = account.member?.status || "active";
      return applyFilterOperation(memberStatus, operator, value);
    },
  },
  identityMfaStatus: {
    type: "identityMfaStatus",
    label: "MFA Status",
    icon: Shield,
    operators: ["equals"],
    valueType: "select",
    options: [
      { label: "Enabled", value: "enabled" },
      { label: "Disabled", value: "disabled" },
    ],
    description: "Filter by MFA enablement status",
    applyFilter: (account, operator, value) => {
      const mfaStatus = account.identity?.mfa_enabled ? "enabled" : "disabled";
      return applyFilterOperation(mfaStatus, operator, value);
    },
  },
  authType: {
    type: "authType",
    label: "Auth Type",
    icon: Shield,
    operators: ["equals", "isOneOf"],
    valueType: "select",
    options: [
      { label: "SSO", value: "sso" },
      { label: "Password", value: "password" },
      { label: "Oauth", value: "oauth" },
    ],
    description: "Filter by authentication type",
    applyFilter: (account, operator, value) => {
      const authType = account.identity?.auth_type || "";
      return applyFilterOperation(authType, operator, value);
    },
  },
};

/**
 * Helper function to apply filter operations
 */
export const applyFilterOperation = (
  value: string,
  operator: FilterOperator,
  filterValue: any,
): boolean => {
  // Handle null/undefined values safely
  const safeValue = value || "";

  switch (operator) {
    case "equals":
      return (
        safeValue.toLowerCase() ===
        (filterValue ? filterValue.toLowerCase() : "")
      );

    case "contains":
      return filterValue
        ? safeValue.toLowerCase().includes(filterValue.toLowerCase())
        : false;

    case "doesNotEqual":
      return (
        safeValue.toLowerCase() !==
        (filterValue ? filterValue.toLowerCase() : "")
      );

    case "doesNotContain":
      return filterValue
        ? !safeValue.toLowerCase().includes(filterValue.toLowerCase())
        : true;

    case "isOneOf":
      if (Array.isArray(filterValue)) {
        return filterValue.some((v) =>
          v ? safeValue.toLowerCase().includes(v.toLowerCase()) : false,
        );
      }
      return false;

    case "isEmpty":
      return (
        !safeValue ||
        (Array.isArray(safeValue)
          ? safeValue.length === 0
          : safeValue?.trim() === "")
      );

    case "isNotEmpty":
      return (
        !!safeValue &&
        !(Array.isArray(safeValue)
          ? safeValue.length === 0
          : safeValue?.trim() === "")
      );

    default:
      return true;
  }
};

/**
 * Apply all filters to an account
 */
export const applyFilters = (
  account: MFReviewAccount,
  filters: Array<{
    type: FilterType;
    operator: FilterOperator;
    value: any;
  }>,
): boolean => {
  if (!filters || filters.length === 0) {
    return true;
  }

  return filters.every((filter) => {
    const filterConfig = FILTERS_REGISTRY[filter.type];
    if (!filterConfig) {
      return true; // Skip unknown filters
    }

    try {
      const result = filterConfig.applyFilter(
        account,
        filter.operator,
        filter.value,
      );
      return result;
    } catch (e) {
      console.error(e, "Failed to handle filter");
      return true;
    }
  });
};

/**
 * Get all available filter types
 */
export const getAvailableFilterTypes = (): FilterType[] => {
  return Object.keys(FILTERS_REGISTRY) as FilterType[];
};

/**
 * Get human-readable operator labels
 */
export const operatorLabels: Record<FilterOperator, string> = {
  equals: "=",
  contains: "contains",
  doesNotEqual: "≠",
  doesNotContain: "!contains",
  isOneOf: "in",
  before: "<",
  after: ">",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
};

/**
 * Generate a human-readable filter label
 */
/**
 * Full operator labels (more descriptive for UI purposes)
 */
export const fullOperatorLabels: Record<FilterOperator, string> = {
  equals: "equals",
  contains: "contains",
  doesNotEqual: "does not equal",
  doesNotContain: "does not contain",
  isOneOf: "is one of",
  before: "before",
  after: "after",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
};

/**
 * Generate a human-readable filter label
 */
export const generateFilterLabel = (filter: {
  type: FilterType;
  operator: FilterOperator;
  value: any;
}): string => {
  const { type, operator, value } = filter;
  const filterConfig = FILTERS_REGISTRY[type];
  if (!filterConfig) return "";

  const typeLabel = filterConfig.label;
  const operatorLabel = fullOperatorLabels[operator];

  if (operator === "isEmpty" || operator === "isNotEmpty") {
    return `${typeLabel} ${operatorLabel}`;
  }

  if (Array.isArray(value)) {
    return `${typeLabel} ${operatorLabel} [${value.filter((v) => v).join(", ") || "empty"}]`;
  }

  // Ensure value is displayed properly
  const displayValue = value === null || value === undefined ? "empty" : value;
  return `${typeLabel} ${operatorLabel} ${displayValue}`;
};

/**
 * True when two filter lists describe the same filters, regardless of array or
 * element identity.
 *
 * `useFilters` mirrors its `externalFilters` argument into state from an effect,
 * and keys that effect off this predicate rather than the array's reference.
 * Callers routinely build the argument inline (`someMap[id] || []`), so a
 * reference check re-runs the effect on every render of the caller, and each run
 * stores a new array — which re-renders the hook's owner, which rebuilds the
 * inline array, which re-runs the effect. That loop is unbounded; comparing by
 * value ends it at the first no-op. (PLO-5463)
 */
export const sameFilterList = (
  a: FilterCondition[],
  b: FilterCondition[],
): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((filter, i) => {
    const other = b[i];
    return (
      filter.id === other.id &&
      filter.type === other.type &&
      filter.operator === other.operator &&
      // Compared even though it is normally derived from the three fields
      // above: callers may pass a custom label, and skipping the state update
      // on a genuinely changed one would leave a stale chip on screen.
      filter.label === other.label &&
      // Filter values are scalars or arrays of scalars (see FILTERS_REGISTRY),
      // so a shallow compare of array members is exact, not a heuristic.
      (Array.isArray(filter.value) && Array.isArray(other.value)
        ? filter.value.length === other.value.length &&
          filter.value.every((v, vi) => v === other.value[vi])
        : filter.value === other.value)
    );
  });
};
